import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import QuestionEditor from './QuestionEditor';
import BalanceCurve from './BalanceCurve';
import AnswerDistributionChart from './AnswerDistributionChart';
import {
    deleteQuestion,
    fromServerQuestion,
    loadActiveShow,
    loadPlaysets,
    loadQuestions,
    resetPlayedQuestions,
    subscribeActiveShow,
    updateQuestion,
} from '../integrations/quizApiAdapter';
import socket from '../socket';
import './css/QuestionsView.css';

Modal.setAppElement('#root');

const defaultQuestion = {
    frage: '',
    antworten: ['', '', '', ''],
    richtigeAntwort: 0,
    kategorie: '',
    schwierigkeitsgrad: 1,
    attachments: [],
    note: '',
    playsetIds: [],
    lastPlayedAt: null,
};

const DIFF_CLASSES = { 1: 'd1', 2: 'd2', 3: 'd3' };

function useDebounce(value, delay = 220) {
    const [deb, setDeb] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDeb(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return deb;
}

function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// ── Sortier-Pfeil ──────────────────────────────────────────
function SortTh({ label, col, sortCol, sortDir, onSort, className = '' }) {
    const active = sortCol === col;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return (
        <div
            className={`qv-th${active ? ' sorted' : ''} ${className}`}
            onClick={() => onSort(col)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSort(col)}
            aria-label={`${label} – ${active ? (sortDir === 'asc' ? 'aufsteigend sortiert' : 'absteigend sortiert') : 'sortieren'}`}
        >
            {label}
            <span className="qv-th-arrow">{arrow}</span>
        </div>
    );
}

// ── Kategorie-Multiselect-Dropdown ─────────────────────────
function CategoryDropdown({ categories, selected, onChange }) {
    const [query, setQuery] = useState('');
    const [open, setOpen]   = useState(false);

    const filtered = useMemo(() => {
        if (!query.trim()) return categories;
        return categories.filter((c) => c.toLowerCase().includes(query.toLowerCase()));
    }, [categories, query]);

    const toggle = (cat) =>
        onChange(selected.includes(cat) ? selected.filter((c) => c !== cat) : [...selected, cat]);

    return (
        <div
            className="qv-cat-dropdown"
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
        >
            <div className="qv-cat-input-wrap" onClick={() => setOpen(true)}>
                {selected.map((cat) => (
                    <span key={cat} className="qv-cat-chip">
                        {cat}
                        <button
                            type="button"
                            onMouseDown={(e) => { e.stopPropagation(); toggle(cat); }}
                            aria-label={`${cat} entfernen`}
                        >✕</button>
                    </span>
                ))}
                <input
                    className="qv-cat-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    placeholder={selected.length === 0 ? 'Kategorie filtern…' : ''}
                    aria-label="Kategorie suchen"
                />
            </div>
            {open && filtered.length > 0 && (
                <div className="qv-cat-list">
                    {filtered.map((cat) => (
                        <button
                            key={cat}
                            type="button"
                            className={`qv-cat-list-item${selected.includes(cat) ? ' selected' : ''}`}
                            onMouseDown={() => toggle(cat)}
                        >
                            <span className="check">{selected.includes(cat) ? '✓' : ' '}</span>
                            {cat}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Hauptkomponente ────────────────────────────────────────
function QuestionsView({ lockedPlaysetId = null } = {}) {
    const [questions, setQuestions]       = useState([]);
    const [loadingQ, setLoadingQ]         = useState(true);
    const [questionsErr, setQuestionsErr] = useState('');
    const [playsets, setPlaysets]         = useState([]);

    // Aktive Show
    const [activePlaysetId, setActivePlaysetId] = useState(null);
    const [activeShowName, setActiveShowName]   = useState('');

    // Welche Uebersicht im Balance-Panel angezeigt wird
    const [balanceView, setBalanceView] = useState('schwierigkeit'); // 'schwierigkeit' | 'antworten'

    // Balance: auf Desktop (>900px) standardmäßig offen, auf Nicht-Desktop geschlossen
    const [balanceOpen, setBalanceOpen] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth > 900 : true
    );

    // Filter-Drawer (nur mobil relevant – Desktop zeigt die Filter immer)
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Filter
    const [searchText,      setSearchText]  = useState('');
    const [filterDiff,      setFilterDiff]  = useState('');
    const [filterCats,      setFilterCats]  = useState([]);
    const [filterPlaysetId, setFilterPlaysetId] = useState(lockedPlaysetId ? String(lockedPlaysetId) : '');
    const [filterStatus,    setFilterStatus] = useState('');

    // Sortierung (via Spaltenköpfe)
    const [sortCol, setSortCol] = useState(null);     // 'frage'|'kategorie'|'schwierigkeitsgrad'|'lastPlayedAt'
    const [sortDir, setSortDir] = useState('asc');    // 'asc'|'desc'

    const debouncedSearch = useDebounce(searchText);

    // Editor
    const [editorOpen,      setEditorOpen]      = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    // Bulk
    const [selectedIds, setSelectedIds] = useState(new Set());
    // bulkPanel: null | 'showset' | 'difficulty'
    const [bulkPanel,   setBulkPanel]   = useState(null);
    const [bulkPsId,    setBulkPsId]    = useState('');

    // ── Daten ───────────────────────────────────────────────
    const fetchQuestions = useCallback(async () => {
        setLoadingQ(true); setQuestionsErr('');
        try { setQuestions(await loadQuestions()); }
        catch (err) { setQuestionsErr(err?.message || 'Fehler'); }
        finally { setLoadingQ(false); }
    }, []);

    useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

    useEffect(() => {
        loadPlaysets().then((d) => setPlaysets(Array.isArray(d) ? d : [])).catch(() => {});
    }, []);

    useEffect(() => {
        loadActiveShow()
            .then((r) => setActivePlaysetId(r?.playsetId ? Number(r.playsetId) : null))
            .catch(() => {});
        const unsub = subscribeActiveShow(socket, (id) => setActivePlaysetId(id ? Number(id) : null));
        return unsub;
    }, []);

    useEffect(() => {
        if (!activePlaysetId || !playsets.length) { setActiveShowName(''); return; }
        const s = playsets.find((p) => p.id === activePlaysetId);
        setActiveShowName(s?.name || '');
    }, [activePlaysetId, playsets]);

    useEffect(() => {
        if (lockedPlaysetId) setFilterPlaysetId(String(lockedPlaysetId));
    }, [lockedPlaysetId]);

    // Balance automatisch einklappen wenn Viewport auf ≤900px schrumpft
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth <= 900) setBalanceOpen(false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Browser-Zurück (Wisch-Geste links) schließt den Editor statt zurück zu navigieren
    useEffect(() => {
        const onPopState = () => {
            setEditorOpen(prev => (prev ? false : prev));
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // ── Computed ────────────────────────────────────────────
    const allCategories = useMemo(() => {
        const s = new Set();
        questions.forEach((q) => { if (q.kategorie) s.add(q.kategorie); });
        return [...s].sort();
    }, [questions]);

    const filteredQuestions = useMemo(() => {
        let r = questions;
        if (debouncedSearch.trim()) {
            const s = debouncedSearch.toLowerCase();
            r = r.filter((q) =>
                q.frage.toLowerCase().includes(s) ||
                (q.antworten || []).some((a) => a.toLowerCase().includes(s))
            );
        }
        if (filterDiff)          r = r.filter((q) => String(q.schwierigkeitsgrad) === filterDiff);
        if (filterCats.length)   r = r.filter((q) => filterCats.includes(q.kategorie));
        if (filterPlaysetId)     r = r.filter((q) => (q.playsetIds || []).map(String).includes(filterPlaysetId));
        if (filterStatus === 'gespielt') r = r.filter((q) =>  q.gespielt);
        if (filterStatus === 'offen')    r = r.filter((q) => !q.gespielt);

        // Spalten-Sortierung
        if (sortCol) {
            r = [...r].sort((a, b) => {
                let va, vb;
                if (sortCol === 'frage')             { va = a.frage || ''; vb = b.frage || ''; }
                else if (sortCol === 'kategorie')     { va = a.kategorie || ''; vb = b.kategorie || ''; }
                else if (sortCol === 'schwierigkeitsgrad') { va = a.schwierigkeitsgrad; vb = b.schwierigkeitsgrad; }
                else if (sortCol === 'lastPlayedAt')  { va = a.lastPlayedAt || ''; vb = b.lastPlayedAt || ''; }
                if (va < vb) return sortDir === 'asc' ? -1 : 1;
                if (va > vb) return sortDir === 'asc' ?  1 : -1;
                return 0;
            });
        }
        return r;
    }, [questions, debouncedSearch, filterDiff, filterCats, filterPlaysetId, filterStatus, sortCol, sortDir]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    // Showsets für Filter-Dropdown (aktive zuerst, dann archivierte)
    const { activePs, archivedPs } = useMemo(() => ({
        activePs:   playsets.filter((p) => p.status !== 'archived'),
        archivedPs: playsets.filter((p) => p.status === 'archived'),
    }), [playsets]);

    // Fragen der aktiven Show (für Balance)
    const activeShowQuestions = useMemo(() => {
        if (!activePlaysetId) return [];
        return questions.filter((q) => (q.playsetIds || []).map(Number).includes(activePlaysetId));
    }, [questions, activePlaysetId]);

    // ── Editor ──────────────────────────────────────────────
    const openEditor = useCallback((q) => {
        setEditingQuestion(q ? fromServerQuestion(q) : null);
        setEditorOpen(true);
        // Synthetischer History-Eintrag: Browser-Zurück / Wisch-Geste schließt Modal statt zu navigieren
        window.history.pushState({ qvEditorOpen: true }, '');
    }, []);

    const closeEditor = useCallback(() => {
        setEditorOpen(false);
        setEditingQuestion(null);
        // History-Eintrag aufräumen, falls wir ihn selbst gepusht haben
        if (window.history.state?.qvEditorOpen) {
            window.history.back();
        }
    }, []);

    const handleOpenEdit = useCallback((q) => openEditor(q), [openEditor]);

    const handleQuestionSaved = useCallback((saved) => {
        setQuestions((prev) => {
            const exists = prev.some((q) => q.id === saved.id);
            return exists ? prev.map((q) => (q.id === saved.id ? saved : q)) : [...prev, saved];
        });
        closeEditor();
    }, [closeEditor]);

    const handleSaveAndNext = useCallback(() => { /* Modal bleibt offen */ }, []);

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Frage wirklich löschen?')) return;
        try {
            await deleteQuestion(id);
            setQuestions((p) => p.filter((q) => q.id !== id));
            setSelectedIds((p) => { const n = new Set(p); n.delete(id); return n; });
        } catch {}
    };

    // ── Auswahl ─────────────────────────────────────────────
    const toggleSelect = (id, e) => {
        e.stopPropagation();
        setSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    };
    const toggleAll = () =>
        setSelectedIds(selectedIds.size === filteredQuestions.length
            ? new Set()
            : new Set(filteredQuestions.map((q) => q.id))
        );

    // ── Bulk ────────────────────────────────────────────────
    const handleBulkDelete = async () => {
        if (!window.confirm(`${selectedIds.size} Fragen wirklich löschen?`)) return;
        for (const id of selectedIds) { try { await deleteQuestion(id); } catch {} }
        setQuestions((p) => p.filter((q) => !selectedIds.has(q.id)));
        setSelectedIds(new Set());
    };

    const handleBulkResetStatus = async () => {
        for (const id of selectedIds) {
            const q = questions.find((x) => x.id === id);
            if (!q) continue;
            try {
                const saved = await updateQuestion(id, { ...q, gespielt: false, lastPlayedAt: null });
                setQuestions((p) => p.map((x) => (x.id === id ? saved : x)));
            } catch {}
        }
        setSelectedIds(new Set());
    };

    const handleBulkAssignPlayset = async () => {
        if (!bulkPsId) return;
        for (const id of selectedIds) {
            const q = questions.find((x) => x.id === id);
            if (!q) continue;
            const ids = (q.playsetIds || []).map(Number);
            const newId = Number(bulkPsId);
            if (ids.includes(newId)) continue;
            try {
                const saved = await updateQuestion(id, { ...q, playsetIds: [...ids, newId] });
                setQuestions((p) => p.map((x) => (x.id === id ? saved : x)));
            } catch {}
        }
        setBulkPanel(null); setBulkPsId(''); setSelectedIds(new Set());
    };

    const handleBulkSetDifficulty = async (lvl) => {
        for (const id of selectedIds) {
            const q = questions.find((x) => x.id === id);
            if (!q) continue;
            try {
                const saved = await updateQuestion(id, { ...q, schwierigkeitsgrad: lvl });
                setQuestions((p) => p.map((x) => (x.id === id ? saved : x)));
            } catch {}
        }
        setBulkPanel(null); setSelectedIds(new Set());
    };

    const handleResetAll = async () => {
        if (!window.confirm('Gespielt-Status ALLER Fragen zurücksetzen?')) return;
        try {
            await resetPlayedQuestions();
            setQuestions((p) => p.map((q) => ({ ...q, gespielt: false, lastPlayedAt: null })));
        } catch {}
    };

    // Anzahl aktiver Filter (für den mobilen Drawer-Toggle-Badge)
    const activeFilterCount =
        (searchText.trim() ? 1 : 0) +
        (filterDiff ? 1 : 0) +
        filterCats.length +
        (filterPlaysetId && !lockedPlaysetId ? 1 : 0) +
        (filterStatus ? 1 : 0);

    const editorKey = editingQuestion ? editingQuestion.id : 'new';

    return (
        <div className="page section-stack qv-page" style={{ paddingBottom: 80 }}>

            {/* ── Seitenheader ─────────────────────────────── */}
            <div className="qv-header">
                <div className="qv-header-title">
                    <h2 className="card-title" style={{ fontSize: '22px' }}>Fragenkatalog</h2>
                    <span className="text-muted">{questions.length} Fragen gesamt</span>
                </div>
                <div className="qv-header-actions">
                    <button className="btn btn-ghost btn-sm" onClick={handleResetAll}>
                        Status zurücksetzen
                    </button>
                    <button className="btn btn-primary" onClick={() => openEditor(null)}>
                        + Neue Frage
                    </button>
                </div>
            </div>

            {/* ── Top-Container: Filter + Balance ──────────── */}
            <div className="qv-top-container">

                {/* Filter-Bereich – balance-closed-Klasse für padding-right-Transition */}
                <div className={`qv-filter-section${balanceOpen ? '' : ' balance-closed'}`}>

                    {/* Drawer-Toggle (nur mobil sichtbar) */}
                    <button
                        type="button"
                        className="qv-filter-toggle"
                        onClick={() => setFiltersOpen((o) => !o)}
                        aria-expanded={filtersOpen}
                    >
                        <svg className="qv-filter-toggle-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="4"/><line x1="9.5" y1="9.5" x2="13.5" y2="13.5"/></svg>
                        <span className="qv-filter-toggle-label">Suche &amp; Filter</span>
                        {activeFilterCount > 0 && (
                            <span className="qv-filter-toggle-badge">{activeFilterCount}</span>
                        )}
                        <span className={`qv-filter-toggle-arrow${filtersOpen ? ' open' : ''}`}>▾</span>
                    </button>

                    {/* Filter-Body – Desktop immer sichtbar, Mobil per Drawer ein-/ausklappbar */}
                    <div className={`qv-filter-body${filtersOpen ? ' open' : ''}`}>
                    {/* Suche */}
                    <div className="qv-filter-search">
                        <svg className="qv-filter-search-icon" viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="6.5" cy="6.5" r="4"/><line x1="9.5" y1="9.5" x2="13.5" y2="13.5"/></svg>
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Fragen und Antworten durchsuchen…"
                        />
                    </div>

                    {/* Zeile 1: Schwierigkeit + Status */}
                    <div className="qv-filter-row">
                        <div className="qv-filter-group">
                            <label>Schwierigkeit</label>
                            <div className="qv-segmented">
                                <button className={filterDiff === '' ? 'active' : ''} onClick={() => setFilterDiff('')}>Alle</button>
                                {[1, 2, 3].map((d) => (
                                    <button
                                        key={d}
                                        className={filterDiff === String(d) ? 'active' : ''}
                                        onClick={() => setFilterDiff(filterDiff === String(d) ? '' : String(d))}
                                    >★{d}</button>
                                ))}
                            </div>
                        </div>

                        <div className="qv-filter-group">
                            <label>Status</label>
                            <div className="qv-segmented">
                                {[['', 'Alle'], ['offen', 'Offen'], ['gespielt', 'Gespielt']].map(([v, l]) => (
                                    <button key={v} className={filterStatus === v ? 'active' : ''} onClick={() => setFilterStatus(v)}>{l}</button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Zeile 2: Showset + Kategorie */}
                    <div className="qv-filter-row">
                        <div className="qv-filter-group" style={{ flex: 1, minWidth: 110 }}>
                            <label>Showset</label>
                            <select
                                value={filterPlaysetId}
                                onChange={(e) => setFilterPlaysetId(e.target.value)}
                                disabled={Boolean(lockedPlaysetId)}
                                style={{ padding: '7px 10px', fontSize: '13px' }}
                            >
                                <option value="">Alle</option>
                                {activePs.map((ps) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
                                {archivedPs.length > 0 && (
                                    <optgroup label="── Archiviert ──">
                                        {archivedPs.map((ps) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
                                    </optgroup>
                                )}
                            </select>
                        </div>
                        {allCategories.length > 0 && (
                            <div className="qv-filter-group" style={{ flex: 2, minWidth: 160 }}>
                                <label>Kategorie</label>
                                <CategoryDropdown
                                    categories={allCategories}
                                    selected={filterCats}
                                    onChange={setFilterCats}
                                />
                            </div>
                        )}
                    </div>
                    </div>{/* /qv-filter-body */}
                </div>

                {/* Balance-Panel */}
                <div className={`qv-balance-panel${balanceOpen ? '' : ' collapsed'}`}>
                    <div className="qv-balance-content">
                        {balanceOpen ? (
                            <>
                                <div className="qv-balance-header">
                                    <span className="qv-balance-title">Balance</span>
                                    <button
                                        className="qv-balance-collapse-btn"
                                        onClick={() => setBalanceOpen(false)}
                                        aria-label="Balance einklappen"
                                    >
                                        ◀◀ Einklappen
                                    </button>
                                </div>
                                <div
                                    className="qv-balance-switch"
                                    role="group"
                                    aria-label="Ansicht wählen"
                                >
                                    <button
                                        type="button"
                                        className={balanceView === 'schwierigkeit' ? 'active' : ''}
                                        onClick={() => setBalanceView('schwierigkeit')}
                                    >
                                        Schwierigkeit
                                    </button>
                                    <button
                                        type="button"
                                        className={balanceView === 'antworten' ? 'active' : ''}
                                        onClick={() => setBalanceView('antworten')}
                                    >
                                        Antworten
                                    </button>
                                </div>
                                {activePlaysetId ? (
                                    <>
                                        {activeShowName && (
                                            <div className="qv-balance-show-name">{activeShowName}</div>
                                        )}
                                        {balanceView === 'schwierigkeit' ? (
                                            <BalanceCurve questions={activeShowQuestions} />
                                        ) : (
                                            <AnswerDistributionChart questions={activeShowQuestions} />
                                        )}
                                    </>
                                ) : (
                                    <div className="qv-balance-empty">Keine Show ausgewählt</div>
                                )}
                            </>
                        ) : (
                            <button
                                className="qv-balance-collapse-btn"
                                onClick={() => setBalanceOpen(true)}
                                aria-label="Balance ausklappen"
                            >
                                ▶▶ Ausklappen
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Fragenübersicht (volle Breite) ───────────── */}
            <div className="card" style={{ padding: '12px' }}>
                {questionsErr && (
                    <div style={{ background: 'rgba(248,113,113,0.1)', color: '#f87171', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <span style={{ flex: 1 }}>{questionsErr}</span>
                        <button className="btn btn-ghost btn-sm" onClick={fetchQuestions}>Retry</button>
                    </div>
                )}

                {loadingQ ? (
                    <div className="text-muted" style={{ padding: '32px', textAlign: 'center' }}>Laden…</div>
                ) : filteredQuestions.length === 0 ? (
                    <div className="qv-empty">
                        <div style={{ marginBottom: '8px', opacity: 0.5 }}>
                            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="10" cy="10" r="6"/><line x1="14" y1="14" x2="20" y2="20"/></svg>
                        </div>
                        <div>Keine Fragen gefunden</div>
                        {(searchText || filterDiff || filterCats.length || filterStatus) && (
                            <button className="btn btn-ghost btn-sm" style={{ marginTop: '12px' }}
                                onClick={() => { setSearchText(''); setFilterDiff(''); setFilterCats([]); setFilterStatus(''); }}>
                                Alle Filter zurücksetzen
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Tabellenkopf (sortierbar) */}
                        <div className="qv-table-header">
                            {/* Checkbox "alle auswählen" */}
                            <div className="qv-th qv-th-check" style={{ cursor: 'default' }}>
                                <input
                                    type="checkbox"
                                    className="qv-row-check"
                                    checked={selectedIds.size === filteredQuestions.length && filteredQuestions.length > 0}
                                    onChange={toggleAll}
                                    aria-label="Alle auswählen"
                                />
                            </div>

                            <SortTh label="Frage"    col="frage"             sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="qv-col-frage" />
                            <SortTh label="Kategorie" col="kategorie"         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="qv-col-kategorie" />
                            <SortTh label="Schwer."  col="schwierigkeitsgrad" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="qv-col-diff" />
                            <SortTh label="Gespielt" col="lastPlayedAt"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} className="qv-col-played" />

                            {/* Trefferzahl rechts */}
                            <span className="qv-count-badge" style={{ marginLeft: 'auto' }}>
                                {filteredQuestions.length} / {questions.length}
                            </span>
                        </div>

                        {/* Zeilen */}
                        <div className="qv-list">
                            {filteredQuestions.map((q) => (
                                <div
                                    key={q.id}
                                    className={`qv-row${selectedIds.has(q.id) ? ' selected' : ''}`}
                                    onClick={() => handleOpenEdit(q)}
                                >
                                    {/* Checkbox-Bereich – stopPropagation verhindert Öffnen des Editors */}
                                    <div
                                        className="qv-row-check-wrap"
                                        onClick={(e) => { e.stopPropagation(); toggleSelect(q.id, e); }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="qv-row-check"
                                            checked={selectedIds.has(q.id)}
                                            onChange={(e) => toggleSelect(q.id, e)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Frage auswählen: ${q.frage}`}
                                        />
                                    </div>

                                    {/* Fragetext – kein eigener onClick mehr (Zeile ist clickable) */}
                                    <span
                                        className="qv-row-text qv-col-frage"
                                        title={q.frage}
                                    >
                                        {q.frage}
                                    </span>

                                    {/* Kategorie */}
                                    <div className="qv-row-kategorie qv-col-kategorie">
                                        {q.kategorie ? (
                                            <span className="chip muted" style={{ fontSize: '11px' }}>{q.kategorie}</span>
                                        ) : null}
                                    </div>

                                    {/* Schwierigkeit */}
                                    <div className="qv-row-diff qv-col-diff">
                                        <span className={`qv-diff-badge ${DIFF_CLASSES[q.schwierigkeitsgrad] || 'd2'}`}>
                                            ★{q.schwierigkeitsgrad}
                                        </span>
                                    </div>

                                    {/* Zuletzt gespielt */}
                                    <div className="qv-row-played qv-col-played">
                                        {formatDate(q.lastPlayedAt)}
                                    </div>

                                    {/* Löschen-Button */}
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={(e) => handleDelete(q.id, e)}
                                        title="Löschen"
                                        aria-label="Frage löschen"
                                        style={{ color: '#f87171', flexShrink: 0, padding: '6px 8px' }}
                                    >
                                        <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor">
                                            <path d="M6.5 1.5h3a.5.5 0 010 1h-3a.5.5 0 010-1z"/>
                                            <rect x="1.5" y="3.5" width="13" height="1.5" rx=".75"/>
                                            <path d="M3.5 6l.8 8.5h8.4L13.5 6z"/>
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* ── Bulk-Leiste ──────────────────────────────── */}
            {selectedIds.size > 0 && (
                <div className="qv-bulk-bar">
                    <span className="qv-bulk-count">{selectedIds.size} ausgewählt</span>

                    {/* Standard-Aktionen (wenn kein Sub-Panel offen) */}
                    {bulkPanel === null && (
                        <>
                            <button className="btn btn-secondary btn-sm" onClick={() => setBulkPanel('showset')}>
                                Showset zuweisen
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => setBulkPanel('difficulty')}>
                                Schwierigkeit setzen
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={handleBulkResetStatus}>
                                Status zurücksetzen
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                                Löschen
                            </button>
                        </>
                    )}

                    {/* Showset-Zuweisung */}
                    {bulkPanel === 'showset' && (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                            <select
                                value={bulkPsId}
                                onChange={(e) => setBulkPsId(e.target.value)}
                                style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '8px', background: '#0c1223', color: 'var(--text)', border: '1px solid var(--border)', minWidth: 0, width: 'auto' }}
                            >
                                <option value="">Showset wählen…</option>
                                {activePs.map((ps) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
                                {archivedPs.length > 0 && (
                                    <optgroup label="── Archiviert ──">
                                        {archivedPs.map((ps) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
                                    </optgroup>
                                )}
                            </select>
                            <button className="btn btn-primary btn-sm" onClick={handleBulkAssignPlayset} disabled={!bulkPsId}>
                                Zuweisen
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => { setBulkPanel(null); setBulkPsId(''); }}>✕</button>
                        </div>
                    )}

                    {/* Schwierigkeit setzen */}
                    {bulkPanel === 'difficulty' && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                            <span style={{ fontSize: '13px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Setzen auf:</span>
                            {[1, 2, 3].map((lvl) => (
                                <button
                                    key={lvl}
                                    className={`qv-bulk-diff-btn d${lvl}`}
                                    onClick={() => handleBulkSetDifficulty(lvl)}
                                >
                                    ★{lvl}
                                </button>
                            ))}
                            <button className="btn btn-ghost btn-sm" onClick={() => setBulkPanel(null)}>✕</button>
                        </div>
                    )}

                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { setSelectedIds(new Set()); setBulkPanel(null); }}>
                        Auswahl aufheben
                    </button>
                </div>
            )}

            {/* ── Editor-Modal ─────────────────────────────── */}
            <Modal
                isOpen={editorOpen}
                onRequestClose={closeEditor}
                className="modal-content qv-editor-modal"
                overlayClassName="modal-overlay"
                contentLabel={editingQuestion ? 'Frage bearbeiten' : 'Neue Frage anlegen'}
                shouldFocusAfterRender={false}
            >
                <QuestionEditor
                    key={editorKey}
                    isEditing={!!editingQuestion}
                    questionId={editingQuestion?.id || null}
                    initialQuestion={editingQuestion || defaultQuestion}
                    playsets={playsets}
                    existingCategories={allCategories}
                    onQuestionSaved={handleQuestionSaved}
                    onSaveAndNext={handleSaveAndNext}
                    onCancelEdit={closeEditor}
                />
            </Modal>
        </div>
    );
}

export default QuestionsView;
